import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button,
} from '@mui/material'
import FileIcon from '@mui/icons-material/InsertDriveFile'
import ImageIcon from '@mui/icons-material/Image'
import CodeIcon from '@mui/icons-material/Code'
import CsvIcon from '@mui/icons-material/TableChart'
import ChartIcon from '@mui/icons-material/BarChart'
import ReportIcon from '@mui/icons-material/Description'
import DownloadIcon from '@mui/icons-material/Download'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'axios'

import { AgentArtifactWidgetData, SelectedConversationItem } from '../../../types/gotypes'
import { ArtifactInfo, AgentSessionListItem, RenderedTableInfo, RenderedChartInfo, ResolvedSelectedItem } from '../../../types/agent'
import type { AgentSSEEvent, PersistedAgentMessage } from '../../../types/agent'
import { API_BASE_URL } from '../../../constants/system'
import { getLanguageFromFilename } from '../../../utils/artifactFilters'
import { replayEvents, replayFromPersistedMessages } from '../../../agent'
import { AgentChat, InlineArtifactPreview, AgentMarkdown } from '../../../agent'
import InlinePlatinumTable from '../../agent/InlinePlatinumTable'
import { getArtifactImageUrl } from '../../../utils/chartDataUtils'

// ==========================================
// Renderer
// ==========================================

interface RendererProps {
  data: AgentArtifactWidgetData
}

const ARTIFACT_ICONS: Record<string, React.ElementType> = {
  file: FileIcon,
  image: ImageIcon,
  code: CodeIcon,
  csv: CsvIcon,
  chart: ChartIcon,
  report: ReportIcon,
}

export const AgentArtifactWidgetRenderer: React.FC<RendererProps> = ({ data }) => {
  // Use selected items mode when available
  if (data.selected_items && data.selected_items.length > 0) {
    return <SelectedItemsRenderer data={data} />
  }

  // Legacy grid/carousel mode
  return <LegacyArtifactRenderer data={data} />
}

// ==========================================
// Selected Items Renderer (new full-fidelity mode)
// ==========================================

function SelectedItemsRenderer({ data }: { data: AgentArtifactWidgetData }) {
  const [resolved, setResolved] = useState<ResolvedSelectedItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The widget's pinned items are static config. They are read by the fetch
  // below but must not re-trigger it, so they ride a ref rather than the
  // dependency array.
  const itemsRef = useRef(data.selected_items || [])
  itemsRef.current = data.selected_items || []

  // The server resolves the pinned items against the session and returns only
  // their content. This deliberately does NOT fetch /query-events or
  // /messages: those carry the whole conversation, internal discussion
  // included, and a dashboard viewer needs none of it to draw three tables
  // (#1043).
  useEffect(() => {
    let cancelled = false
    axios
      .post<{ items: ResolvedSelectedItem[] }>(
        `${API_BASE_URL}/agent/session/${data.session_id}/selected-items`,
        { items: itemsRef.current }
      )
      .then((res) => {
        if (cancelled) return
        setResolved(res.data.items || [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('Session no longer available')
        } else {
          setError('Failed to load session data')
        }
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [data.session_id])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="warning">{error}</Alert>
  }

  if (!resolved) return null

  const title = data.title || 'Agent Results'

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.selected_items!.map((item, idx) => (
          <SelectedItemView
            key={`${item.kind}-${item.tool_call_id || item.message_id || item.artifact_id || idx}`}
            item={item}
            resolved={resolved[idx]}
            sessionId={data.session_id}
          />
        ))}
      </Box>
    </Box>
  )
}

function SelectedItemView({ item, resolved, sessionId }: {
  item: SelectedConversationItem
  resolved: ResolvedSelectedItem | undefined
  sessionId: string
}) {
  if (item.kind === 'table' && item.tool_call_id) {
    // Matching (by tool_call_id, then by position among the selected items of
    // the same kind) now happens server-side — see goapi/pkg/agentselect.
    const table = resolved?.table
    if (table) {
      return (
        <InlinePlatinumTable
          platinumData={table.platinumData}
          title={table.title}
          customer={table.customer}
          job={table.job}
          spec={table.spec}
        />
      )
    }
    return <Alert severity="info" sx={{ fontSize: 13 }}>Table not found</Alert>
  }

  if (item.kind === 'chart' && item.tool_call_id) {
    const chart = resolved?.chart
    if (chart) {
      const chartTypeMap: Record<string, string> = { bar: 'bar', line: 'line', pie: 'pie', stacked: 'stackedColumn' }
      return (
        <InlinePlatinumTable
          platinumData={chart.platinumData}
          title={chart.title}
          customer={chart.customer}
          job={chart.job}
          spec={chart.spec}
          defaultViewMode="graph"
          defaultChartType={chartTypeMap[chart.chartType] || 'bar'}
        />
      )
    }
    return <Alert severity="info" sx={{ fontSize: 13 }}>Chart not found</Alert>
  }

  if (item.kind === 'image') {
    const artifact = resolved?.artifact
    if (artifact) {
      const imgUrl = getArtifactImageUrl(artifact, sessionId, API_BASE_URL)
      if (imgUrl) {
        return (
          <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <img src={imgUrl} alt={item.label} style={{ display: 'block', maxWidth: '100%' }} />
          </Box>
        )
      }
    }
    return <Alert severity="info" sx={{ fontSize: 13 }}>Image not found</Alert>
  }

  if (item.kind === 'webapp') {
    const artifact = resolved?.artifact
    if (artifact) {
      return (
        <InlineArtifactPreview
          artifact={artifact}
          sessionId={sessionId}
          onOpenPreview={() => {}}
        />
      )
    }
    return <Alert severity="info" sx={{ fontSize: 13 }}>Web app not found</Alert>
  }

  if (item.kind === 'text' && item.content) {
    return (
      <Box sx={{
        p: '10px 14px',
        borderRadius: '12px',
        backgroundColor: '#f3f4f6',
        fontSize: 14,
        lineHeight: 1.5,
      }}>
        <AgentMarkdown content={item.content} />
      </Box>
    )
  }

  return null
}

// ==========================================
// Legacy Artifact Renderer (grid/carousel)
// ==========================================

function LegacyArtifactRenderer({ data }: { data: AgentArtifactWidgetData }) {
  const [artifacts, setArtifacts] = useState<ArtifactInfo[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionGone, setSessionGone] = useState(false)

  useEffect(() => {
    let cancelled = false
    axios
      .get<{ artifacts: ArtifactInfo[] }>(
        `${API_BASE_URL}/agent/session/${data.session_id}/artifacts`
      )
      .then((res) => {
        if (cancelled) return
        setArtifacts(res.data.artifacts || [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setSessionGone(true)
        } else {
          setError('Failed to load artifacts')
        }
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [data.session_id])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (sessionGone) {
    return <Alert severity="warning">Session no longer available</Alert>
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  let filtered = artifacts || []
  if (data.artifact_filter && data.artifact_filter.length > 0) {
    filtered = filtered.filter((a) => data.artifact_filter!.includes(a.artifactType))
  }

  const maxItems = data.max_items || 6
  const display = filtered.slice(0, maxItems)
  const title = data.title || 'Artifacts'

  if (display.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">No artifacts found</Typography>
      </Box>
    )
  }

  if (data.layout === 'carousel') {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <CarouselView artifacts={display} />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <GridView artifacts={display} />
    </Box>
  )
}

// ==========================================
// Grid View
// ==========================================

function GridView({ artifacts }: { artifacts: ArtifactInfo[] }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 1.5,
      }}
    >
      {artifacts.map((artifact) => (
        <ArtifactCard key={artifact.id || artifact.filePath} artifact={artifact} />
      ))}
    </Box>
  )
}

// ==========================================
// Carousel View
// ==========================================

function CarouselView({ artifacts }: { artifacts: ArtifactInfo[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const amount = 220
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }, [])

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        aria-label="scroll left"
        onClick={() => scroll('left')}
        size="small"
        sx={{ position: 'absolute', left: -4, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'white', boxShadow: 1 }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          px: 3,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {artifacts.map((artifact) => (
          <Box key={artifact.id || artifact.filePath} sx={{ minWidth: 200, flexShrink: 0 }}>
            <ArtifactCard artifact={artifact} />
          </Box>
        ))}
      </Box>

      <IconButton
        aria-label="scroll right"
        onClick={() => scroll('right')}
        size="small"
        sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'white', boxShadow: 1 }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

// ==========================================
// Artifact Card (shared between grid/carousel)
// ==========================================

function ArtifactCard({ artifact }: { artifact: ArtifactInfo }) {
  const Icon = ARTIFACT_ICONS[artifact.artifactType] || FileIcon
  const isLost = artifact.status === 'lost'

  const handleDownload = useCallback(() => {
    if (isLost || !artifact.id) return
    const downloadUrl = `${API_BASE_URL}/agent/artifacts/${artifact.id}/download`
    const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined
    fetch(downloadUrl, {
      headers: authHeader ? { Authorization: authHeader } : {},
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = artifact.fileName
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((err) => console.error('Download failed:', err))
  }, [artifact, isLost])

  return (
    <Box
      data-testid="artifact-card"
      onClick={handleDownload}
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        p: 1.5,
        cursor: isLost ? 'default' : 'pointer',
        backgroundColor: 'white',
        opacity: isLost ? 0.5 : 1,
        '&:hover': isLost ? {} : { borderColor: '#3b82f6', backgroundColor: '#f8fafc' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Icon sx={{ fontSize: 18, color: '#6b7280' }} />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: isLost ? 'line-through' : 'none',
          }}
        >
          {artifact.label}
        </Typography>
      </Box>

      {artifact.artifactType === 'code' && (
        <Box sx={{ mb: 0.5 }}>
          <Chip
            label={getLanguageFromFilename(artifact.fileName)}
            size="small"
            sx={{ height: 18, fontSize: 10, fontWeight: 500, backgroundColor: '#e2e8f0', color: '#475569' }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
          {artifact.fileName}
        </Typography>
        {!isLost && (
          <DownloadIcon sx={{ fontSize: 14, color: '#2563eb' }} />
        )}
      </Box>
    </Box>
  )
}

// ==========================================
// Editor
// ==========================================

interface EditorProps {
  customer: string
  data: AgentArtifactWidgetData | undefined
  onDataChange: (data: AgentArtifactWidgetData) => void
}

export const AgentArtifactWidgetEditor: React.FC<EditorProps> = ({
  customer,
  data,
  onDataChange,
}) => {
  const [sessions, setSessions] = useState<AgentSessionListItem[]>([])
  const [loadingSessions, setLoadingSessions] = useState(true)

  useEffect(() => {
    let cancelled = false
    axios
      .get<{ sessions: AgentSessionListItem[] }>(
        `${API_BASE_URL}/agent/sessions?customer=${encodeURIComponent(customer)}`
      )
      .then((res) => {
        if (cancelled) return
        setSessions(res.data.sessions || [])
        setLoadingSessions(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoadingSessions(false)
      })
    return () => { cancelled = true }
  }, [customer])

  const currentData: AgentArtifactWidgetData = data || {
    session_id: '',
    layout: 'grid',
    max_items: 6,
  }

  const hasSession = Boolean(currentData.session_id)

  if (!hasSession) {
    return (
      <SessionList
        sessions={sessions}
        loading={loadingSessions}
        onSelect={(sessionId) => onDataChange({ ...currentData, session_id: sessionId, selected_items: [] })}
      />
    )
  }

  return (
    <SessionConversationEditor
      currentData={currentData}
      onDataChange={onDataChange}
      sessions={sessions}
    />
  )
}

// ==========================================
// State A: Session List
// ==========================================

function SessionList({ sessions, loading, onSelect }: {
  sessions: AgentSessionListItem[]
  loading: boolean
  onSelect: (sessionId: string) => void
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (sessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No agent sessions found
      </Typography>
    )
  }

  const STATUS_COLORS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
    completed: 'success',
    active: 'info',
    streaming: 'info',
    creating: 'warning',
    error: 'error',
    cancelled: 'default',
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Select a Session
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 1 }}>
        {sessions.map((session) => (
          <Box
            key={session.id}
            onClick={() => onSelect(session.id)}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              borderBottom: '1px solid #f3f4f6',
              '&:hover': { backgroundColor: '#f0f7ff' },
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                {session.title || `Session ${session.id.slice(0, 8)}`}
              </Typography>
              <Chip
                label={session.status}
                size="small"
                color={STATUS_COLORS[session.status] || 'default'}
                sx={{ height: 20, fontSize: 10 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {session.workflow_id && (
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  {session.workflow_id}
                </Typography>
              )}
              {session.persona && (
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  &middot; {session.persona}
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 'auto' }}>
                {new Date(session.created_at * 1000).toLocaleDateString()}
              </Typography>
              {session.artifact_count > 0 && (
                <Chip
                  label={`${session.artifact_count} artifacts`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ==========================================
// State B: Session Conversation Editor
// ==========================================

function SessionConversationEditor({ currentData, onDataChange, sessions }: {
  currentData: AgentArtifactWidgetData
  onDataChange: (data: AgentArtifactWidgetData) => void
  sessions: AgentSessionListItem[]
}) {
  const sessionInfo = sessions.find(s => s.id === currentData.session_id)
  const selectedItems = currentData.selected_items || []

  // Load session data for conversation viewer
  const [messages, setMessages] = useState<import('../../../types/agent').AgentMessage[]>([])
  const [artifacts, setArtifacts] = useState<ArtifactInfo[]>([])
  const [renderedTables, setRenderedTables] = useState<Map<string, RenderedTableInfo>>(new Map())
  const [renderedCharts, setRenderedCharts] = useState<Map<string, RenderedChartInfo>>(new Map())
  const [askedQuestions, setAskedQuestions] = useState<Map<string, import('../../../types/agent').AskUserQuestionInfo>>(new Map())
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentData.session_id) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      axios.get<{ events: Array<{ events: unknown[] }> }>(
        `${API_BASE_URL}/agent/session/${currentData.session_id}/query-events`
      ).catch(() => ({ data: { events: [] } })),
      axios.get<{ messages: PersistedAgentMessage[] }>(
        `${API_BASE_URL}/agent/session/${currentData.session_id}/messages?limit=500`
      ),
      axios.get<{ artifacts: ArtifactInfo[] }>(
        `${API_BASE_URL}/agent/session/${currentData.session_id}/artifacts`
      ),
    ])
      .then(([eventsRes, messagesRes, artifactsRes]) => {
        if (cancelled) return
        const queryEvents = eventsRes.data.events || []
        let restored
        if (queryEvents.length > 0) {
          const allEvents = queryEvents.flatMap(qe => qe.events as AgentSSEEvent[])
          restored = replayEvents(allEvents)
        } else {
          const persisted = messagesRes.data.messages || []
          restored = replayFromPersistedMessages(persisted)
        }

        setMessages(restored.messages)
        setArtifacts(artifactsRes.data.artifacts || [])
        setRenderedTables(restored.renderedTables)
        setRenderedCharts(restored.renderedCharts)
        setAskedQuestions(restored.askedQuestions)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [currentData.session_id])

  const removeItem = useCallback((idx: number) => {
    const next = selectedItems.filter((_, i) => i !== idx)
    onDataChange({ ...currentData, selected_items: next })
  }, [selectedItems, currentData, onDataChange])

  const handleChangeSession = useCallback(() => {
    onDataChange({ ...currentData, session_id: '', selected_items: [] })
  }, [currentData, onDataChange])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          {sessionInfo?.title || `Session ${currentData.session_id.slice(0, 8)}`}
        </Typography>
        {selectedItems.length > 0 && (
          <Chip
            label={`${selectedItems.length} selected`}
            size="small"
            color="info"
            sx={{ height: 22, fontSize: 11 }}
          />
        )}
        <Button
          size="small"
          onClick={handleChangeSession}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          Change Session
        </Button>
      </Box>

      {/* Selected items strip */}
      {selectedItems.length > 0 && (
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          py: 1,
          overflowX: 'auto',
          flexShrink: 0,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          {selectedItems.map((item, idx) => (
            <Chip
              key={`sel-${idx}`}
              label={item.label}
              size="small"
              onDelete={() => removeItem(idx)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 24,
                fontSize: 11,
                flexShrink: 0,
                backgroundColor: '#eff6ff',
                borderColor: '#bfdbfe',
                border: '1px solid',
              }}
            />
          ))}
        </Box>
      )}

      {/* Conversation viewer */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', mt: 1 }}>
          <AgentChat
            messages={messages}
            isStreaming={false}
            error={null}
            artifacts={artifacts}
            renderedTables={renderedTables}
            renderedCharts={renderedCharts}
            askedQuestions={askedQuestions}
            createdDashboards={new Map()}
            sessionId={currentData.session_id}
            readOnly
          />
        </Box>
      )}
    </Box>
  )
}

